/**
 * Level version handshake (API `level_outdated`, docs/ops/native-apps.md). The server keeps
 * every level version it served; a round played on a version it never served, or on one it
 * replaced more than 30 days ago, is refused as `level_outdated` (POST /v1/rounds and
 * /v1/sync; event rounds answer 422) with the version it plays now. The server does not
 * record that refusal. For the client it is neither a cheat nor a loss:
 *  - the round leaves the outbox (it is never retried forever) and its local prediction is
 *    undone: the snapshot resets the server-owned part and the local fail streak forgets it;
 *  - a level the client fetched at runtime (a city pack) is refreshed and the same round is
 *    sent once more, which succeeds when the server learned that version during a rolling
 *    deploy;
 *  - otherwise the player is told, once per session, that an update is available
 *    (features/update).
 */
import { errorCode, HttpError } from './api.js';
import { uuid } from './ids.js';
import type { LevelRepo } from './levels.js';
import type { OutboxOp } from './state.js';
import type { PlayerStore } from './store.js';
import type { SyncService } from './sync.js';

export const LEVEL_OUTDATED = 'level_outdated';

/** The version the server plays now (`currentLevel` of the refusal). */
export interface CurrentLevel {
  levelId: string;
  levelHash: string;
  packVersion: number | null;
}

/** `currentLevel` of a refusal body (a round response or an error body), when it is well formed. */
export function currentLevelOf(body: unknown): CurrentLevel | null {
  const c = (body as { currentLevel?: unknown } | null)?.currentLevel as Partial<CurrentLevel> | undefined;
  if (!c || typeof c.levelId !== 'string' || typeof c.levelHash !== 'string') return null;
  return { levelId: c.levelId, levelHash: c.levelHash, packVersion: typeof c.packVersion === 'number' ? c.packVersion : null };
}

export interface OutdatedDeps {
  levels: LevelRepo;
  store: PlayerStore;
  /** late-bound: the sync service reports its refusals here */
  sync: () => SyncService;
}

export class OutdatedLevels {
  /** level ids the server plays a newer version of (with that version when it said) */
  readonly levels = new Map<string, CurrentLevel | null>();
  private needsUpdate = false;
  private shown = false;
  private listeners = new Set<() => void>();

  constructor(private d: OutdatedDeps) {}

  /** The "update available" message is due: an outdated level could not be fixed here, and it was not shown this session. */
  get noticeDue(): boolean {
    return this.needsUpdate && !this.shown;
  }

  /** The message was shown: not again this session. */
  noticeShown(): void {
    this.shown = true;
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * The server answered level_outdated for `levelId` (`current`: the version it plays now).
   * `fixable`: the client is refreshing that level itself, so no update message for it.
   */
  report(levelId: string | null, current: CurrentLevel | null = null, fixable = false): void {
    const id = levelId ?? current?.levelId ?? null;
    if (id) this.levels.set(id, current ?? this.levels.get(id) ?? null);
    if (!fixable) this.needsUpdate = true;
    for (const fn of this.listeners) fn();
  }

  /**
   * A queued round POST /v1/sync refused as level_outdated (the sync service already took it
   * out of the outbox). A runtime level is refreshed and the round queued once more under a
   * new op id: the server recorded that op's refusal, not the round. Anything else, or a round
   * refused again, is dropped for good.
   */
  async rejected(op: OutboxOp): Promise<'resent' | 'dropped'> {
    if (op.kind !== 'round') return 'dropped';
    const levelId = op.body.header.levelId;
    if (!op.resent && this.d.levels.runtime(levelId)) {
      this.report(levelId, null, true);
      await this.d.levels.refresh(levelId).catch(() => undefined);
      const { held: _h, ...rest } = op;
      this.d.sync().enqueue({ ...rest, opId: uuid(), resent: true });
      return 'resent';
    }
    this.report(levelId);
    // not a loss: GP-09 assist counts fails the server counted, and it never counted this one
    if (op.body.claimed.result === 'fail')
      this.d.store.update((s) => {
        const n = (s.failStreak[levelId] ?? 0) - 1;
        if (n > 0) s.failStreak[levelId] = n;
        else delete s.failStreak[levelId];
      });
    return 'dropped';
  }
}

/**
 * Send a feature round (an event round answers 422 level_outdated); when the level turns out
 * outdated, refresh it where it came from (`refresh`, e.g. the feature reloads its event
 * levels) and send the same round once more. A second refusal is thrown to the caller.
 */
export async function sendOnceMore<T>(send: () => Promise<T>, refresh: (current: CurrentLevel | null) => Promise<unknown>): Promise<T> {
  try {
    return await send();
  } catch (e) {
    if (errorCode(e) !== LEVEL_OUTDATED) throw e;
    await refresh(e instanceof HttpError ? currentLevelOf(e.body) : null);
    return send();
  }
}
